import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';
import * as AiIcons from 'react-icons/ai';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiCheck, FiArrowRight, FiClock, FiTarget, FiTrendingUp, FiUsers, FiStar, FiChevronDown, FiMessageCircle, FiPlay, FiDownload, FiBookOpen, FiDollarSign, FiZap, FiShield } = FiIcons;
const { BiRocket } = BiIcons;
const { AiFillStar } = AiIcons;

function App() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  const [activeAccordion, setActiveAccordion] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-white/5 to-green-500/5 opacity-20"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-8 pb-20">
          {/* Badge */}
          <motion.div 
            className="flex justify-center mb-8"
            {...fadeInUp}
          >
            <div className="bg-gradient-to-r from-orange-500 via-white to-green-500 p-[2px] rounded-full">
              <div className="bg-black px-6 py-2 rounded-full">
                <span className="text-sm font-semibold">🇮🇳 Republic Day Today-Only Offer</span>
              </div>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.div 
            className="text-center mb-12"
            variants={staggerChildren}
            initial="initial"
            animate="animate"
          >
            <motion.h1 
              className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
              variants={fadeInUp}
            >
              Stop Working for Money.<br />
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                Start Building Assets
              </span><br />
              That Pay You Forever.
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
              variants={fadeInUp}
            >
              Freedom Forever Mastery × DMC is a 4-week implementation membership to build your first income-producing digital asset with funnels, automation & community support.
            </motion.p>
          </motion.div>

          {/* Price Block */}
          <motion.div 
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-8 max-w-2xl mx-auto border border-white/20"
            variants={fadeInUp}
          >
            <div className="text-center">
              <div className="flex justify-center items-center gap-4 mb-4">
                <span className="text-2xl text-gray-400 line-through">₹49,997</span>
                <span className="text-2xl text-gray-400 line-through">₹19,997</span>
              </div>
              <div className="text-6xl md:text-7xl font-bold text-yellow-400 mb-2">₹4,997</div>
              <p className="text-lg text-gray-300">Today Only - Republic Day Special</p>
            </div>
          </motion.div>

          {/* Trust Chips */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 mb-8"
            variants={fadeInUp}
          >
            {[
              "Beginner Friendly",
              "No Team Needed", 
              "Templates Included",
              "Tamil-friendly Support"
            ].map((chip, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm border border-white/20">
                <SafeIcon icon={FiCheck} className="inline mr-2 text-green-400" />
                {chip}
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
            variants={fadeInUp}
          >
            <a 
              href="https://abdul.cc/ffm"
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 py-4 rounded-2xl font-bold text-lg hover:scale-105 transition-transform duration-200 flex items-center justify-center"
            >
              Join Freedom Forever Mastery – ₹4,997
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </a>
            
            <a 
              href="https://wa.me/XXXXXXXXXX"
              className="border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-white/10 transition-colors duration-200 flex items-center justify-center"
            >
              <SafeIcon icon={FiMessageCircle} className="mr-2" />
              Talk to Team on WhatsApp
            </a>
          </motion.div>

          {/* Tamil Hook */}
          <motion.p 
            className="text-center text-yellow-400 font-semibold text-lg"
            variants={fadeInUp}
          >
            இன்னைக்கு முடிவு பண்ணுங்க… Freedom starts now.
          </motion.p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Salary + EMI Trap
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                "Work harder every year… savings barely grow. 📉",
                "Inflation up. Fees up. Health costs up. 📈",
                "If you stop working for 3 months… what happens? 😰"
              ].map((point, index) => (
                <div key={index} className="text-xl text-gray-700 p-4 bg-white rounded-xl shadow-sm">
                  {point}
                </div>
              ))}
            </motion.div>

            <motion.div 
              className="bg-red-50 border-l-4 border-red-500 p-8 rounded-xl"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-red-800 mb-6">Cost of Inaction</h3>
              <ul className="space-y-3">
                {[
                  "Same income 3 years later",
                  "No assets",
                  "Retirement fear", 
                  "Still chasing EMI closure",
                  "Regret watching AI-builders win"
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-red-700">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shift Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Your New Identity: <span className="text-yellow-400">Asset Builder</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              { from: "Time", to: "Assets", icon: FiClock },
              { from: "Stress", to: "Systems", icon: FiTarget },
              { from: "Random income", to: "Repeatable income", icon: FiTrendingUp }
            ].map((shift, index) => (
              <motion.div 
                key={index}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl text-center border border-white/20"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <SafeIcon icon={shift.icon} className="text-4xl text-yellow-400 mb-4 mx-auto" />
                <div className="text-xl font-semibold mb-2">{shift.from}</div>
                <SafeIcon icon={FiArrowRight} className="text-yellow-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-400">{shift.to}</div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 p-8 rounded-2xl text-center border border-yellow-500/30"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-xl md:text-2xl leading-relaxed">
              By the end of 4 weeks, you'll have one validated digital product + a live funnel + automation that can generate income even while you sleep.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What It Is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What is Freedom Forever Mastery × DMC?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">4-Week Implementation Membership</h3>
                <p className="text-gray-700">Structured, week-by-week system to build your first income-producing digital asset.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Faceless Digital Products</h3>
                <p className="text-gray-700">PDFs, checklists, trackers, prompts, WhatsApp courses - no camera needed.</p>
              </div>
            </motion.div>

            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Automation</h3>
                <p className="text-gray-700">Funnel + payments + WhatsApp automation that works 24/7.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">DMC Community</h3>
                <p className="text-gray-700">Accountability, peer support, and expert guidance inside Digital Millionaires Community.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You'll Achieve Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              What You'll Build in <span className="text-yellow-400">4 Weeks</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              { icon: FiTarget, text: "Freedom Number plan (your monthly expense target)" },
              { icon: FiZap, text: "One micro-niche + offer clarity" },
              { icon: FiBookOpen, text: "One faceless digital product" },
              { icon: FiDollarSign, text: "Sales page + checkout" },
              { icon: FiMessageCircle, text: "WhatsApp follow-up automation" },
              { icon: BiRocket, text: "Validation + launch plan" },
              { icon: FiTrendingUp, text: "90-day monetization roadmap" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="flex items-center space-x-4 bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <SafeIcon icon={item.icon} className="text-2xl text-yellow-400 flex-shrink-0" />
                <span className="text-lg">{item.text}</span>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="bg-yellow-500/20 border border-yellow-500/30 p-8 rounded-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-yellow-400 mb-4">See it, believe it</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 p-6 rounded-xl text-center">
                <SafeIcon icon={FiPlay} className="text-3xl text-yellow-400 mb-3 mx-auto" />
                <p>Funnel Demo (placeholder video)</p>
              </div>
              <div className="bg-white/10 p-6 rounded-xl text-center">
                <SafeIcon icon={FiMessageCircle} className="text-3xl text-yellow-400 mb-3 mx-auto" />
                <p>WhatsApp Automation Flow (placeholder image)</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4-Week Roadmap */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The 4-Week Roadmap
            </h2>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                week: "Week 1",
                title: "Foundation & Clarity",
                description: "Micro-niche + problem selection + Freedom Number calculation",
                color: "from-blue-500 to-blue-600"
              },
              {
                week: "Week 2", 
                title: "Product Creation",
                description: "Product creation (AI-assisted) + packaging + positioning",
                color: "from-purple-500 to-purple-600"
              },
              {
                week: "Week 3",
                title: "Systems & Automation",
                description: "Funnel setup + payment integration + WhatsApp automation",
                color: "from-green-500 to-green-600"
              },
              {
                week: "Week 4",
                title: "Launch & Scale",
                description: "Validation + launch strategy + scaling plan (organic + ads basics)",
                color: "from-yellow-500 to-yellow-600"
              }
            ].map((week, index) => (
              <motion.div 
                key={index}
                className="flex flex-col md:flex-row items-center gap-8"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className={`bg-gradient-to-r ${week.color} text-white p-8 rounded-2xl min-w-[300px]`}>
                  <h3 className="text-2xl font-bold mb-2">{week.week}</h3>
                  <h4 className="text-xl font-semibold mb-3">{week.title}</h4>
                  <p className="text-lg opacity-90">{week.description}</p>
                </div>
                {index < 3 && (
                  <SafeIcon icon={FiArrowRight} className="text-3xl text-gray-400 hidden md:block" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              ROI: Recover ₹4,997 Fast
            </h2>
          </motion.div>

          <motion.div 
            className="bg-white p-8 rounded-2xl shadow-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Simple Math Example</h3>
              <div className="bg-green-50 p-6 rounded-xl mb-4">
                <div className="text-lg text-gray-700 mb-2">Sell a ₹999 product to 10 people</div>
                <div className="text-3xl font-bold text-green-600">= ₹9,990</div>
                <div className="text-lg text-gray-700 mt-2">Recover ₹4,997 in 1 day</div>
              </div>
              <p className="text-sm text-gray-600 italic">
                This is not a guarantee—this is simple math. We focus on building real assets and improving execution.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Offer Stack */}
      <section className="py-20 bg-gradient-to-br from-black to-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Everything You Get <span className="text-yellow-400">Today</span>
            </h2>
          </motion.div>

          {/* Core Program */}
          <motion.div 
            className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-2 border-yellow-500/50 p-8 rounded-2xl mb-12"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="text-center">
              <h3 className="text-3xl font-bold text-yellow-400 mb-4">Core Program</h3>
              <h4 className="text-2xl font-bold mb-4">Freedom Forever Mastery (4 Weeks)</h4>
              <div className="text-4xl font-bold text-yellow-400 mb-4">Worth ₹49,997</div>
              <p className="text-lg text-gray-300">Live sessions, assignments, templates, community access, and personal audits</p>
            </div>
          </motion.div>

          {/* Bonus Vault */}
          <div className="mb-12">
            <h3 className="text-3xl font-bold text-center mb-8">
              <span className="text-yellow-400">Bonus Vault</span> - Premium Add-ons
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Micro-Audience Monetization Arsenal™",
                  value: "₹7,997",
                  description: "21 Psychological Triggers listicle + Micro-Audience Monetization prompts"
                },
                {
                  title: "MicroSaaS Wealth Blueprint™ Toolkit",
                  value: "₹12,997", 
                  description: "Complete checklist + prompts + comprehensive book"
                },
                {
                  title: "Rapid Micro Product Factory™",
                  value: "₹9,997",
                  description: "Creation checklist + validation checklist + development prompts + 7-day process guide"
                },
                {
                  title: "Faceless Creator Empire™ Pack",
                  value: "₹14,997",
                  description: "Complete checklist + book + guide + workbook + prompts for faceless success"
                },
                {
                  title: "Micro Monetization Audio Vault™",
                  value: "₹5,997",
                  description: "Micro Monetization Lab podcast + Micro-Offer Method podcast series"
                },
                {
                  title: "Digital Creator's Buddy™ Toolstack",
                  value: "₹6,997",
                  description: "Curated toolstack specifically designed for digital creators"
                },
                {
                  title: "4-D Micro Product Framework™",
                  value: "₹4,997",
                  description: "Comprehensive framework guide for product development"
                },
                {
                  title: "Customer Touchpoint Domination™",
                  value: "₹5,997",
                  description: "21 customer touchpoints listicle for maximum engagement"
                },
                {
                  title: "First Digital Product Launch OS™",
                  value: "₹6,997",
                  description: "Launch checklist + create micro products ebook"
                }
              ].map((bonus, index) => (
                <motion.div 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="text-lg font-bold text-yellow-400 mb-2">Bonus {index + 1}</div>
                  <h4 className="text-xl font-semibold mb-3">{bonus.title}</h4>
                  <div className="text-2xl font-bold text-green-400 mb-3">Worth {bonus.value}</div>
                  <p className="text-gray-300 text-sm">{bonus.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Total Value */}
          <motion.div 
            className="bg-white/10 backdrop-blur-sm border-2 border-white/30 p-8 rounded-2xl text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-4">Total Value</h3>
            <div className="text-5xl font-bold text-green-400 mb-4">₹1,27,000+</div>
            <div className="text-xl text-gray-300 mb-6">Everything above normally costs over ₹1.27 lakhs</div>
            
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black p-6 rounded-xl">
              <div className="text-lg font-semibold mb-2">Today-Only Republic Day Price</div>
              <div className="text-4xl font-bold">₹4,997</div>
              <div className="text-sm mt-2 opacity-80">Save ₹1,22,003 today</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why DMC */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why DMC (Digital Millionaires Community)?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: FiUsers, title: "Accountability + weekly momentum", desc: "Stay on track with weekly check-ins and peer support" },
              { icon: FiStar, title: "Peer support", desc: "Connect with like-minded entrepreneurs on the same journey" },
              { icon: FiDownload, title: "Templates + audits", desc: "Ready-to-use templates and expert feedback on your work" },
              { icon: FiZap, title: "Faster execution", desc: "Proven systems and frameworks to accelerate your progress" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 p-8 rounded-xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <SafeIcon icon={item.icon} className="text-3xl text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-700">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12 p-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-2xl font-bold text-gray-900">
              "You don't need motivation. You need a system."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Who This Is For / Not For
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              className="bg-green-50 border-l-4 border-green-500 p-8 rounded-xl"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-green-800 mb-6 flex items-center">
                <SafeIcon icon={FiCheck} className="mr-3" />
                Perfect For
              </h3>
              <ul className="space-y-3">
                {[
                  "Coaches, consultants, doctors, healers",
                  "Business owners (MSMEs)",
                  "Working professionals seeking passive income", 
                  "Beginners willing to execute and learn"
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-green-700">
                    <SafeIcon icon={FiCheck} className="text-green-600 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="bg-red-50 border-l-4 border-red-500 p-8 rounded-xl"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-red-800 mb-6 flex items-center">
                <span className="text-red-600 mr-3">✕</span>
                Not For
              </h3>
              <ul className="space-y-3">
                {[
                  "People looking for 'get rich quick' schemes",
                  "People who won't implement what they learn",
                  "People expecting results without taking action",
                  "Those not willing to invest time in building"
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-red-700">
                    <span className="text-red-600 mr-3 flex-shrink-0">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Success Stories
            </h2>
            <p className="text-gray-600">Results vary by effort and market conditions</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: "Priya Sharma", profession: "Business Coach", city: "Chennai", result: "Built ₹15K/month passive income stream" },
              { name: "Rajesh Kumar", profession: "Consultant", city: "Bangalore", result: "Automated client onboarding, 3x revenue" },
              { name: "Dr. Meera Patel", profession: "Ayurvedic Doctor", city: "Mumbai", result: "Digital course generates ₹25K monthly" },
              { name: "Arun Krishnan", profession: "IT Professional", city: "Hyderabad", result: "Side business now matches salary" },
              { name: "Kavitha Reddy", profession: "Wellness Coach", city: "Pune", result: "WhatsApp automation saved 20 hours/week" },
              { name: "Suresh Nair", profession: "Business Owner", city: "Kochi", result: "Digital products added ₹50K monthly revenue" }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 p-6 rounded-xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={AiFillStar} className="text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.result}"</p>
                <div className="text-sm text-gray-600">
                  <div className="font-semibold">{testimonial.name}</div>
                  <div>{testimonial.profession}, {testimonial.city}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "I'm a beginner. Can I do this?",
                a: "Absolutely! This program is designed for beginners. We provide step-by-step guidance, templates, and community support. No prior experience needed."
              },
              {
                q: "Do I need to show my face on camera?",
                a: "No! This is specifically designed for faceless digital products. You can build and sell without ever appearing on camera."
              },
              {
                q: "How much time per week do I need?",
                a: "Plan for 5-8 hours per week. This includes live sessions, implementation time, and community interaction. It's designed for busy professionals."
              },
              {
                q: "Do I need to run ads?",
                a: "No ads required initially. We focus on organic methods first. Paid advertising is covered as a scaling option in Week 4."
              },
              {
                q: "What if my niche is confusing?",
                a: "Week 1 is dedicated to niche clarity. We have frameworks and community support to help you identify your profitable micro-niche."
              },
              {
                q: "What exactly is inside DMC?",
                a: "DMC (Digital Millionaires Community) includes weekly accountability sessions, peer networking, template library, expert feedback, and ongoing support."
              },
              {
                q: "Is there a refund policy?",
                a: "We offer a 7-day money-back guarantee if you're not satisfied after attending the first week's content and sessions."
              },
              {
                q: "Will I get support in Tamil?",
                a: "Yes! Our team provides Tamil-friendly support, and many community members are Tamil speakers who can help."
              },
              {
                q: "Can business owners use this too?",
                a: "Definitely! This works great for adding digital revenue streams to existing businesses. Many successful members are business owners."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className={`text-gray-500 transition-transform ${activeAccordion === index ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {activeAccordion === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 text-gray-700">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Today-Only Republic Day Offer
            </h2>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 mb-8">
              <div className="text-2xl font-semibold mb-4 flex items-center justify-center">
                <SafeIcon icon={FiClock} className="mr-3" />
                Offer Ends In:
              </div>
              <div className="flex justify-center gap-4 text-4xl md:text-5xl font-bold">
                <div className="bg-white/20 rounded-lg p-4">
                  <div>{String(timeLeft.hours).padStart(2, '0')}</div>
                  <div className="text-sm font-normal">Hours</div>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <div>{String(timeLeft.minutes).padStart(2, '0')}</div>
                  <div className="text-sm font-normal">Minutes</div>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <div>{String(timeLeft.seconds).padStart(2, '0')}</div>
                  <div className="text-sm font-normal">Seconds</div>
                </div>
              </div>
            </div>

            <p className="text-xl mb-8">
              Bonuses + ₹4,997 price end at midnight tonight
            </p>

            <a 
              href="https://abdul.cc/ffm"
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-2xl font-bold text-xl transition-colors duration-200 inline-flex items-center"
            >
              Secure Your Spot Now – ₹4,997
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Freedom is a Decision.
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Join Freedom Forever Mastery × DMC today and start building assets that pay you every month.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="https://abdul.cc/ffm"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 py-4 rounded-2xl font-bold text-xl hover:scale-105 transition-transform duration-200 flex items-center justify-center"
              >
                Join Now – ₹4,997
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </a>
              
              <a 
                href="https://wa.me/XXXXXXXXXX"
                className="border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-semibold text-xl hover:bg-white/10 transition-colors duration-200 flex items-center justify-center"
              >
                <SafeIcon icon={FiMessageCircle} className="mr-2" />
                WhatsApp Us
              </a>
            </div>

            <p className="text-yellow-400 font-semibold text-lg">
              இன்னைக்கு join பண்ணுங்க… நாளைக்கு assets வேலை செய்யும்.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-white/20 p-4 z-50 md:hidden">
        <div className="flex gap-3">
          <a 
            href="https://abdul.cc/ffm"
            className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black text-center py-3 rounded-xl font-bold"
          >
            Join ₹4,997
          </a>
          <a 
            href="https://wa.me/XXXXXXXXXX"
            className="bg-green-600 text-white px-4 py-3 rounded-xl flex items-center justify-center"
          >
            <SafeIcon icon={FiMessageCircle} />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-400">© Abdul Hakkim A – All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
}

export default App;